import React from 'react'
import { X, Calendar, Target, DollarSign, Users, Building, Briefcase } from 'lucide-react'

const ViewTargetModal = ({ isOpen, target, onClose }) => {
  if (!isOpen || !target) return null

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount).replace('LKR', 'Rs.')
  }

  const formatMonth = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'long',
      year: 'numeric' 
    })
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status) => {
    const statusStyles = {
      achieved: 'bg-green-100 text-green-800 border-green-200',
      not_achieved: 'bg-red-100 text-red-800 border-red-200',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    }
    
    const statusLabels = {
      achieved: 'Achieved',
      not_achieved: 'Not Achieved', 
      in_progress: 'In Progress'
    }

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusStyles[status] || statusStyles.not_achieved}`}>
        {statusLabels[status] || 'Not Achieved'}
      </span>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Target Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2" />
              Target Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Target Name</label>
                <p className="text-gray-900 font-medium">{target.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Target Month</label>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <p className="text-gray-900">{formatMonth(target.targetMonth)}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Type</label>
                <p className="text-gray-900 capitalize">{target.type}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                {getStatusBadge(target.status)}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Target Value</label>
                <div className="flex items-center">
                  <p className="text-gray-900 font-semibold">{formatCurrency(target.targetValue)}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Bonus Amount</label>
                <div className="flex items-center">
                  <p className="text-gray-900 font-semibold">{formatCurrency(target.bonusAmount)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Employees */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Assigned Employees ({target.employees?.length || 0})
            </h3>
            {target.employees && target.employees.length > 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Job
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Branch
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {target.employees.map((employeeData, index) => {
                        // Add null checks for safety
                        if (!employeeData || !employeeData.employee) {
                          return null // Skip this entry if employee data is null
                        }
                        
                        const employee = employeeData.employee
                        const branch = employeeData.branch
                        return (
                          <tr key={employee?._id || `employee-${index}`} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee?._id?.slice(-8).toUpperCase() || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee?.fullName || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {employee?.contactNumber || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <Briefcase className="h-4 w-4 text-gray-400 mr-2" />
                                {employee?.job?.name || employee?.job || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <Building className="h-4 w-4 text-gray-400 mr-2" />
                                {branch?.name || 'N/A'}
                              </div>
                            </td>
                          </tr>
                        )
                      }).filter(Boolean)}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No employees assigned to this target</p>
              </div>
            )}
          </div>

          {/* Meta Information */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Created By</label>
                <p className="text-gray-900">{target.createdBy?.username || 'N/A'}</p>
                <p className="text-xs text-gray-500 capitalize">Role: {target.createdBy?.role || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Created At</label>
                <p className="text-gray-900">{formatDate(target.createdAt)}</p>
              </div>
              {target.updatedBy && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated By</label>
                  <p className="text-gray-900">{target.updatedBy?.username || 'N/A'}</p>
                  <p className="text-xs text-gray-500 capitalize">Role: {target.updatedBy?.role || 'N/A'}</p>
                </div>
              )}
              {target.updatedAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated At</label>
                  <p className="text-gray-900">{formatDate(target.updatedAt)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewTargetModal
